import axios from 'axios';
import { deduplicatedFetch } from './requestDeduplicator';

// Export API_URL for use in contexts and other modules
export const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// In-memory cache for frequently accessed data
const cache = {
  siteCategories: null,
  siteCategoriesTimestamp: 0,
  siteContent: null,
  siteContentTimestamp: 0,
};

// Cache TTL: 60 seconds for categories, 30 seconds for content
const CACHE_TTL = {
  categories: 60 * 1000,
  content: 30 * 1000,
};

/**
 * Resolve an image URL - handles both relative paths and full URLs.
 * Relative paths (like /api/uploads/...) are prefixed with the backend URL.
 * Full URLs (like https://...) are returned as-is.
 */
export const resolveImageUrl = (url) => {
  if (!url) return '';
  // If it's already a full URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // Check if it's pointing to a different domain (old preview URL)
    // and extract just the path if so
    try {
      const urlObj = new URL(url);
      const currentHost = new URL(API_URL).host;
      if (urlObj.host !== currentHost && url.includes('/api/uploads/')) {
        // Extract just the relative path
        const path = urlObj.pathname;
        return `${API_URL}${path}`;
      }
    } catch (e) {
      // URL parsing failed, return original
    }
    return url;
  }
  // Relative path - prepend backend URL
  if (url.startsWith('/')) {
    return `${API_URL}${url}`;
  }
  return url;
};

// ============ PUBLIC API ============

// Product Groups (Public)
export const getProductGroups = async (params = {}) => {
  const response = await apiClient.get('/product-groups', { params });
  // Handle both paginated response and legacy array response
  return response.data;
};

// Get product groups with pagination info
export const getProductGroupsPaginated = async (params = {}) => {
  const response = await apiClient.get('/product-groups', { params });
  // New paginated format: { items, total, has_more, limit, offset }
  return response.data;
};

export const getFeaturedProductGroups = async (region) => {
  const params = { show_on_homepage: true, limit: 8 };
  if (region) params.region = region;
  const response = await apiClient.get('/product-groups', { params });
  // Extract items from paginated response
  return response.data.items || response.data;
};

export const getEditorsPicksProducts = async (region) => {
  const params = { show_on_editors_page: true };
  if (region) params.region = region;
  const response = await apiClient.get('/product-groups', { params });
  // Extract items from paginated response
  return response.data.items || response.data;
};

export const getProductGroupBySlug = async (slug) => {
  const response = await apiClient.get(`/product/${slug}`);
  return response.data;
};

// Categories (Public) - with deduplication and caching
export const getSiteCategories = async () => {
  // Check memory cache first
  const now = Date.now();
  if (cache.siteCategories && (now - cache.siteCategoriesTimestamp) < CACHE_TTL.categories) {
    return cache.siteCategories;
  }
  
  // Use deduplication to prevent concurrent requests
  return deduplicatedFetch('site-categories', async () => {
    const response = await apiClient.get('/site-categories');
    cache.siteCategories = response.data;
    cache.siteCategoriesTimestamp = Date.now();
    return response.data;
  }, 200);
};

export const getSiteCategoryBySlug = async (slug) => {
  const response = await apiClient.get(`/site-categories/${slug}`);
  return response.data;
};

export const getCategoriesFull = async () => {
  const response = await apiClient.get('/categories/full');
  return response.data;
};

// Site Content (Public) - with deduplication and caching
export const getSiteContent = async () => {
  const now = Date.now();
  if (cache.siteContent && (now - cache.siteContentTimestamp) < CACHE_TTL.content) {
    return cache.siteContent;
  }
  
  return deduplicatedFetch('site-content', async () => {
    const response = await apiClient.get('/site-content');
    cache.siteContent = response.data;
    cache.siteContentTimestamp = Date.now();
    return response.data;
  }, 200);
};

// Announcements (Public)
export const getActiveAnnouncements = async () => {
  const response = await apiClient.get('/announcements');
  return response.data;
};

// ============ ADMIN API ============

// Admin Auth
export const adminLogin = async (password) => {
  const response = await apiClient.post('/admin/login', { password });
  return response.data;
};

export const adminVerify = async (token) => {
  const response = await apiClient.get('/admin/verify', { params: { token } });
  return response.data;
};

// Admin Product Groups
export const getAdminProductGroups = async (token, { skip = 0, limit = 500 } = {}) => {
  const response = await apiClient.get('/admin/product-groups', { 
    params: { token, skip, limit } 
  });
  // Handle both old format (array) and new format (paginated object)
  const data = response.data;
  if (Array.isArray(data)) {
    return data;
  }
  return data.items || [];
};

export const getAdminProductGroupById = async (token, groupId) => {
  const response = await apiClient.get(`/admin/product-groups/${groupId}`, { params: { token } });
  return response.data;
};

export const createProductGroup = async (token, groupData) => {
  const response = await apiClient.post('/admin/product-groups', groupData, { params: { token } });
  return response.data;
};

export const updateProductGroup = async (token, groupId, groupData) => {
  const response = await apiClient.put(`/admin/product-groups/${groupId}`, groupData, { params: { token } });
  return response.data;
};

export const deleteProductGroup = async (token, groupId) => {
  const response = await apiClient.delete(`/admin/product-groups/${groupId}`, { params: { token } });
  return response.data;
};

export const reorderProductGroups = async (token, orderData) => {
  const response = await apiClient.post('/admin/product-groups/reorder', orderData, { params: { token } });
  return response.data;
};

// Admin Site Categories
export const getAdminSiteCategories = async (token) => {
  const response = await apiClient.get('/admin/site-categories', { params: { token } });
  return response.data;
};

export const getAdminCategories = async (token) => {
  const response = await apiClient.get('/admin/categories', { params: { token } });
  return response.data;
};

export const createSiteCategory = async (token, categoryData) => {
  const response = await apiClient.post('/admin/site-categories', categoryData, { params: { token } });
  return response.data;
};

export const updateSiteCategory = async (token, categoryId, updates) => {
  const response = await apiClient.put(`/admin/site-categories/${categoryId}`, updates, { params: { token } });
  return response.data;
};

export const deleteSiteCategory = async (token, categoryId) => {
  const response = await apiClient.delete(`/admin/site-categories/${categoryId}`, { params: { token } });
  return response.data;
};

export const reorderSiteCategories = async (token, orderData) => {
  const response = await apiClient.post('/admin/site-categories/reorder', orderData, { params: { token } });
  return response.data;
};

// Admin Subcategories
export const createSubcategory = async (token, categoryId, subcategoryData) => {
  const response = await apiClient.post(`/admin/categories/${categoryId}/subcategories`, subcategoryData, { params: { token } });
  return response.data;
};

export const updateSubcategory = async (token, categoryId, subcategoryId, updates) => {
  const response = await apiClient.put(`/admin/categories/${categoryId}/subcategories/${subcategoryId}`, updates, { params: { token } });
  return response.data;
};

export const deleteSubcategory = async (token, categoryId, subcategoryId) => {
  const response = await apiClient.delete(`/admin/categories/${categoryId}/subcategories/${subcategoryId}`, { params: { token } });
  return response.data;
};

export const reorderSubcategories = async (token, categoryId, orderData) => {
  const response = await apiClient.post(`/admin/categories/${categoryId}/subcategories/reorder`, orderData, { params: { token } });
  return response.data;
};

// Admin Site Content
export const getAdminSiteContent = async (token) => {
  const response = await apiClient.get('/admin/site-content', { params: { token } });
  return response.data;
};

export const updateSiteContent = async (token, contentData) => {
  const response = await apiClient.put('/admin/site-content', contentData, { params: { token } });
  return response.data;
};

// Admin Announcements
export const getAdminAnnouncements = async (token) => {
  const response = await apiClient.get('/admin/announcements', { params: { token } });
  return response.data;
};

export const createAnnouncement = async (token, announcementData) => {
  const response = await apiClient.post('/admin/announcements', announcementData, { params: { token } });
  return response.data;
};

export const updateAnnouncement = async (token, announcementId, updates) => {
  const response = await apiClient.put(`/admin/announcements/${announcementId}`, updates, { params: { token } });
  return response.data;
};

export const deleteAnnouncement = async (token, announcementId) => {
  const response = await apiClient.delete(`/admin/announcements/${announcementId}`, { params: { token } });
  return response.data;
};

export const reorderAnnouncements = async (token, orderData) => {
  const response = await apiClient.post('/admin/announcements/reorder', orderData, { params: { token } });
  return response.data;
};

// Admin Image Upload
export const uploadImage = async (token, file, uploadContext = 'website') => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/admin/upload-image`, formData, {
    params: { token, upload_context: uploadContext },
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  // Cloudinary URLs are already full URLs, local URLs need API_URL prefix
  const url = response.data.url;
  const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
  return { ...response.data, fullUrl };
};

export default apiClient;
